import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { currencyCodes } from '~/globals';
import dayjs from '~/timeInit';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardBackspace from '@mui/icons-material/KeyboardBackspace';
import { LoadingButton } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import SummaryToggleAccordion from '~/components/Common/Forms/SummaryToggleAccordion';
import MarkdownEditor from '~/components/Common/Forms/widgets/MarkdownEditor';
import Spinner from '~/components/Common/Spinner';
import UserSearchWidget from '~/components/Common/UserSearchWidget';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';
import ProductTypeEligibilityHelper from '~/components/PromoV3/ProductTypeEligibilityHelper';
import StandaloneMultiProductOptionSelect from '~/components/PromoV3/StandaloneMultiProductOptionSelect';
import VoucherProductTypeField, {
  inferVoucherProductTypeFromProductCodes,
} from '~/components/Vouchers/VoucherProductTypeField';
import { getVoucherNamePatternError } from '~/components/Vouchers/voucherNameValidation';
import { getVoucherTextReplacementError } from '~/components/Vouchers/voucherTextReplacementValidation';
import { withTenant } from '~/components/hoc';

import { toPromoBrand } from '~/consts/promoBrand';

import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';
import useAppNavigation from '~/hooks/useAppNavigation';

import { CreateVoucherPayload, CreateVoucherResponse, createVoucher, defaultExpiryDays } from '~/services/PromoService';
import { SYDNEY_TIMEZONE } from '~/services/TimeService';

import { normalizeToISODate } from '~/utils/dateFormatter';

type VoucherReward = NonNullable<CreateVoucherPayload['voucher_rewards']>[number];
type VoucherCondition = NonNullable<CreateVoucherPayload['voucher_conditions']>[number];

type AssignRow = { id: string; userId: string; userSearchLabel: string };
type SuccessfullyAddedUser = { userId: string; label: string };

const DEFAULT_REWARD_VALUE = '10';
const DEFAULT_REWARD_PRODUCTS: Array<VoucherReward['products'][number]> = [
  'hotel-hotel',
  'hotel-bedbank_hotel',
  'hotel-tactical_ao_hotel',
  'tour-ALL',
  'cruise-ALL',
];

type VoucherUserExpiryMode = 'days' | 'date';

/** Default fixed user-expiry when using date mode on simple create, matching simple promo create. */
const SIMPLE_CREATE_DEFAULT_EXPIRY_DAYS = 365;

function getDefaultSimpleVoucherFixedExpiryDate(): string {
  return dayjs().tz(SYDNEY_TIMEZONE).add(SIMPLE_CREATE_DEFAULT_EXPIRY_DAYS, 'day').format('YYYY-MM-DD');
}

/** YYYY-MM-DD for API + assign-users, from either date picker or days-from-now. */
function getResolvedSimpleVoucherUserExpiryDate(params: {
  mode: VoucherUserExpiryMode;
  voucherUserExpiresAt: string;
  expiryDays: string;
}): string | null {
  if (params.mode === 'date') {
    const t = params.voucherUserExpiresAt.trim();
    return t ? dayjs(t).format('YYYY-MM-DD') : null;
  }
  const daysStr = params.expiryDays.trim();
  if (daysStr === '') {
    return null;
  }
  const parsed = Number(daysStr);
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    return null;
  }
  return dayjs().tz(SYDNEY_TIMEZONE).add(parsed, 'day').format('YYYY-MM-DD');
}

export type SimpleVoucherFormMode = 'page' | 'popup';

interface Props {
  tenant: App.Tenant;
  /** Full marketing-style layout vs compact body for a dialog. */
  mode?: SimpleVoucherFormMode;
  /** When set (e.g. user page popup), after a voucher is created one assign row is added for this user. */
  prefillAssigneeUserId?: string;
  prefillAssigneeUserLabel?: string;
}

function SimpleVoucherForm({ mode = 'page', prefillAssigneeUserId, prefillAssigneeUserLabel }: Props) {
  const isPopup = mode === 'popup';
  const { push } = useAppNavigation();
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();

  const [voucherTitle, setVoucherTitle] = useState('');
  const [voucherDescription, setVoucherDescription] = useState('');
  const [voucherCallToAction, setVoucherCallToAction] = useState('');
  const [voucherCallToActionUrl, setVoucherCallToActionUrl] = useState('');
  const [voucherName, setVoucherName] = useState('');
  const [voucherStartDate, setVoucherStartDate] = useState(() => dayjs().tz(SYDNEY_TIMEZONE).format('YYYY-MM-DD'));
  const [voucherUserExpiryMode, setVoucherUserExpiryMode] = useState<VoucherUserExpiryMode>('date');
  const [voucherUserExpiresAt, setVoucherUserExpiresAt] = useState(() => getDefaultSimpleVoucherFixedExpiryDate());
  const [expiryDays, setExpiryDays] = useState(() => defaultExpiryDays.toString());
  const [caseId, setCaseId] = useState('');
  const [reason, setReason] = useState('');
  const [currency, setCurrency] = useState('AUD');
  const [value, setValue] = useState(DEFAULT_REWARD_VALUE);
  const [offerId, setOfferId] = useState('');
  const [rewardProducts, setRewardProducts] = useState<Array<VoucherReward['products'][number]>>(() => [
    ...DEFAULT_REWARD_PRODUCTS,
  ]);
  const [minSpendEnabled, setMinSpendEnabled] = useState(false);
  const [minSpendAmount, setMinSpendAmount] = useState('100');
  const [minSpendProducts, setMinSpendProducts] = useState<
    Array<NonNullable<VoucherReward['min_spend_products']>[number]>
  >(() => [...DEFAULT_REWARD_PRODUCTS] as Array<NonNullable<VoucherReward['min_spend_products']>[number]>);
  const [selectedVoucherToggleKeys, setSelectedVoucherToggleKeys] = useState<Array<string>>([]);

  const [createdVoucherId, setCreatedVoucherId] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [assignRows, setAssignRows] = useState<Array<AssignRow>>([]);
  const [successfullyAddedUsers, setSuccessfullyAddedUsers] = useState<Array<SuccessfullyAddedUser>>([]);
  const [voucherNameErrors, setVoucherNameErrors] = useState<string | null>(null);
  const [voucherDescriptionEditorKey, setVoucherDescriptionEditorKey] = useState(0);
  const [descriptionAccordionExpanded, setDescriptionAccordionExpanded] = useState(false);

  const createMutation = useMutation<CreateVoucherResponse, Error, CreateVoucherPayload>({
    mutationFn: (payload) => createVoucher(payload),
  });
  const assignUsersMutation = useCreateVoucherUsers();

  const rewardIdRef = useRef(uuid());
  const rewardRow = useMemo(
    (): VoucherReward => ({
      id_voucher_reward: rewardIdRef.current,
      reward_amount: Number.parseFloat(value) || 0,
      products: rewardProducts,
      currency_code: currency as VoucherReward['currency_code'],
    }),
    [currency, value, rewardProducts],
  );

  const voucherDescriptionSummaryPreview = useMemo(() => {
    const t = voucherDescription.trim();
    if (!t) {
      return 'Optional — supports markdown';
    }
    const singleLine = t.replace(/\s+/g, ' ').trim();
    const maxLen = 120;
    return singleLine.length > maxLen ? `${singleLine.slice(0, maxLen)}…` : singleLine;
  }, [voucherDescription]);

  useEffect(() => {
    const uid = prefillAssigneeUserId?.trim();
    if (!uid) {
      return;
    }
    setAssignRows((rows) => {
      if (rows.length > 0) {
        return rows;
      }
      return [
        {
          id: uuid(),
          userId: uid,
          userSearchLabel: (prefillAssigneeUserLabel ?? '').trim(),
        },
      ];
    });
  }, [prefillAssigneeUserId, prefillAssigneeUserLabel]);

  const addAssignRow = useCallback(() => {
    setAssignRows((rows) => [...rows, { id: uuid(), userId: '', userSearchLabel: '' }]);
  }, []);

  const removeAssignRow = useCallback((id: string) => {
    setAssignRows((rows) => rows.filter((r) => r.id !== id));
  }, []);

  const updateAssignRow = useCallback((id: string, patch: Partial<AssignRow>) => {
    setAssignRows((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const assignedUserIds = useMemo(() => assignRows.map((r) => r.userId.trim()).filter(Boolean), [assignRows]);
  const willAutoAssignOnCreate = !createdVoucherId && assignedUserIds.length > 0;

  const runAssignForVoucherId = useCallback(
    async (voucherId: string, rows: Array<AssignRow>) => {
      const ids = rows.map((r) => r.userId.trim()).filter(Boolean);
      if (ids.length === 0) {
        return;
      }
      const assignDateStr =
        getResolvedSimpleVoucherUserExpiryDate({
          mode: voucherUserExpiryMode,
          voucherUserExpiresAt,
          expiryDays,
        }) ?? getDefaultSimpleVoucherFixedExpiryDate();
      const expiredAt = normalizeToISODate(assignDateStr);
      const labelByUserId = new Map(
        rows.map((row) => {
          const u = row.userId.trim();
          return [u, row.userSearchLabel.trim() || u] as const;
        }),
      );
      const response = await assignUsersMutation.mutateAsync({
        voucher_id: voucherId,
        expired_at: expiredAt,
        voucher_users: ids.map((user_id) => ({ user_id })),
      });
      const r = response.result;
      const created = r?.createdVoucherUsers?.length ?? 0;
      const updated = r?.updatedVoucherUsers?.length ?? 0;
      const reactivated = r?.reactivatedVoucherUsers?.length ?? 0;
      const skipped = r?.skippedVoucherUsers?.length ?? 0;
      const ok = created + updated + reactivated;
      const skippedIds = new Set(
        (r?.skippedVoucherUsers ?? []).map((s: { userId?: string }) => (s.userId ?? '').trim()).filter(Boolean),
      );
      if (ok === 0 && skipped > 0) {
        enqueueSnackbar('No users were assigned (all skipped).', { variant: 'warning' });
      } else if (skipped > 0) {
        enqueueSnackbar(`Assigned ${ok} user(s); ${skipped} skipped.`, { variant: 'warning' });
      } else if (ok <= 1) {
        enqueueSnackbar('Users assigned to this voucher.', { variant: 'success' });
      } else {
        enqueueSnackbar(`${ok} users assigned to this voucher.`, { variant: 'success' });
      }
      if (ok > 0) {
        setSuccessfullyAddedUsers((prev) => {
          const seen = new Set(prev.map((p) => p.userId));
          const next = [...prev];
          for (const userId of ids) {
            if (skippedIds.has(userId)) {
              continue;
            }
            if (seen.has(userId)) {
              continue;
            }
            seen.add(userId);
            next.push({ userId, label: labelByUserId.get(userId) ?? userId });
          }
          return next;
        });
      }
      queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] });
      queryClient.invalidateQueries({ queryKey: ['voucher', voucherId] });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      setAssignRows([]);
    },
    [assignUsersMutation, enqueueSnackbar, queryClient, voucherUserExpiresAt, expiryDays, voucherUserExpiryMode],
  );

  const handleCopyVoucherName = useCallback(
    (name: string) => {
      if (!name) {
        return;
      }
      try {
        void navigator.clipboard.writeText(name);
        enqueueSnackbar('Voucher name copied!', { variant: 'success' });
      } catch (e) {
        console.warn('Failed to copy voucher name to clipboard', { name, e });
      }
    },
    [enqueueSnackbar],
  );

  const validateCreate = useCallback((): string | null => {
    const trimmedCode = voucherName.trim();
    if (!trimmedCode) {
      return 'Enter a voucher name.';
    }
    if (voucherNameErrors) {
      return voucherNameErrors;
    }
    if (!voucherTitle.trim()) {
      return 'Enter a voucher title.';
    }
    const textReplacementError = [voucherTitle, voucherDescription, voucherCallToAction, voucherCallToActionUrl]
      .map(getVoucherTextReplacementError)
      .find(Boolean);
    if (textReplacementError) {
      return textReplacementError;
    }
    if (!voucherStartDate.trim()) {
      return 'Start date is required.';
    }
    const at = voucherUserExpiresAt.trim();
    const daysStr = expiryDays.trim();
    if (voucherUserExpiryMode === 'date') {
      if (!at) {
        return 'Expiry date is required.';
      }
      if (dayjs(voucherStartDate.trim()).isAfter(dayjs(at), 'day')) {
        return 'Expiry date must be on or after the start date.';
      }
    } else if (!daysStr) {
      return 'Enter expiry days from today, or switch to an expiry date.';
    } else {
      const parsed = Number(daysStr);
      if (!Number.isSafeInteger(parsed) || parsed < 0) {
        return 'Enter a valid whole number of days (0 or greater).';
      }
      const resolvedFromDays = getResolvedSimpleVoucherUserExpiryDate({
        mode: 'days',
        voucherUserExpiresAt: '',
        expiryDays: daysStr,
      });
      if (resolvedFromDays && dayjs(voucherStartDate.trim()).isAfter(dayjs(resolvedFromDays), 'day')) {
        return 'Computed expiry (today + expiry days) must be on or after the start date.';
      }
    }
    // Only required on the user page ("user specific" voucher create page)
    if (mode === 'popup') {
      if (!caseId.trim()) {
        return 'Enter a case ID for creating this voucher.';
      }

      if (!reason.trim()) {
        return 'Enter a reason for creating this voucher.';
      }
    }
    const n = Number.parseFloat(value);
    if (!Number.isFinite(n) || n <= 0) {
      return 'Enter a valid reward value greater than zero.';
    }
    if (!rewardProducts.length) {
      return 'Select at least one product line for this voucher.';
    }
    if (minSpendEnabled) {
      const minSpend = Number.parseFloat(minSpendAmount);
      if (!Number.isFinite(minSpend) || minSpend <= 0) {
        return 'Enter a valid minimum spend greater than zero.';
      }
      if (!minSpendProducts.length) {
        return 'Select at least one product line for minimum spend.';
      }
    }
    return null;
  }, [
    mode,
    voucherName,
    voucherNameErrors,
    voucherTitle,
    voucherDescription,
    voucherCallToAction,
    voucherCallToActionUrl,
    voucherStartDate,
    caseId,
    reason,
    value,
    voucherUserExpiresAt,
    expiryDays,
    voucherUserExpiryMode,
    rewardProducts,
    minSpendEnabled,
    minSpendAmount,
    minSpendProducts,
  ]);

  const getVoucherReportingTags = useCallback((mode: string) => {
    if (mode === 'popup') {
      return ['create-voucher-for-specific-user'];
    }
    return [];
  }, []);

  const handleCreate = async () => {
    const err = validateCreate();
    if (err) {
      enqueueSnackbar(err, { variant: 'warning' });
      return;
    }
    const assignRowsForAfterCreate = assignRows;
    const resolvedExpiry = getResolvedSimpleVoucherUserExpiryDate({
      mode: voucherUserExpiryMode,
      voucherUserExpiresAt,
      expiryDays,
    });
    if (!resolvedExpiry) {
      enqueueSnackbar('Could not determine voucher user expiry.', { variant: 'warning' });
      return;
    }

    const promoBrand = toPromoBrand(brand);
    if (!promoBrand) {
      enqueueSnackbar(`Could not get promo brand ${brand}`);
      return;
    }
    const voucher_user_expires_at = normalizeToISODate(resolvedExpiry);

    const trimmedOfferId = offerId.trim();
    const offerCondition: VoucherCondition | undefined = trimmedOfferId
      ? {
          condition_type: 'OFFER-ID-VALIDATION',
          condition_value: trimmedOfferId,
          condition_apply_type: 'MUST-INCLUDE',
          voucher_reward_ids: [],
          customized_message: '',
        }
      : undefined;

    const minSpendProductsForApi = minSpendProducts.filter(Boolean);
    const baseReward: VoucherReward = {
      ...rewardRow,
      reward_amount: Number.parseFloat(value),
      currency_code: currency as VoucherReward['currency_code'],
    };
    const rewardForApi: VoucherReward = minSpendEnabled
      ? {
          ...baseReward,
          min_spend: Number.parseFloat(minSpendAmount),
          min_spend_products: minSpendProductsForApi,
        }
      : baseReward;

    const payload: CreateVoucherPayload = {
      requestSource: 'admin-voucher-simple-form',
      voucher_name: voucherName.trim().toUpperCase(),
      brand: promoBrand,
      voucher_title: voucherTitle.trim(),
      voucher_description: voucherDescription.trim(),
      ...(voucherCallToAction.trim() ? { voucher_call_to_action: voucherCallToAction.trim() } : {}),
      ...(voucherCallToActionUrl.trim() ? { voucher_call_to_action_url: voucherCallToActionUrl.trim() } : {}),
      voucher_rewards: [rewardForApi],
      voucher_conditions: offerCondition ? [offerCondition] : undefined,
      voucher_toggles: selectedVoucherToggleKeys as CreateVoucherPayload['voucher_toggles'],
      theme: 'default',
      product_type: inferVoucherProductTypeFromProductCodes(rewardProducts),
      show_voucher_from_date: normalizeToISODate(voucherStartDate.trim()),
      case_id: caseId.trim(),
      reason: reason.trim(),
      voucher_reporting_tags: getVoucherReportingTags(mode),
      voucher_user_expires_at,
    };

    const idsToAssign = assignRowsForAfterCreate.map((r) => r.userId.trim()).filter(Boolean);
    try {
      const res = await createMutation.mutateAsync(payload);
      const id = res?.result?.voucherId ?? '';
      if (!id) {
        enqueueSnackbar('Voucher created but no id was returned.', { variant: 'warning' });
        return;
      }
      setCreatedVoucherId(id);
      setCreatedCode(voucherName.trim().toUpperCase());
      enqueueSnackbar(`${brand}: Voucher created.`, { variant: 'success' });
      if (idsToAssign.length > 0) {
        try {
          await runAssignForVoucherId(id, assignRowsForAfterCreate);
        } catch (assignErr) {
          enqueueSnackbar(`Assigning user(s) failed: ${(assignErr as Error).message}. The voucher was still created.`, {
            variant: 'error',
          });
        }
      }
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
    } catch (e) {
      enqueueSnackbar(`Failed to create voucher: ${(e as Error).message}`, { variant: 'error' });
    }
  };

  const resetAll = () => {
    rewardIdRef.current = uuid();
    setCreatedVoucherId(null);
    setCreatedCode(null);
    setAssignRows([]);
    setSuccessfullyAddedUsers([]);
    setOfferId('');
    setSelectedVoucherToggleKeys([]);
    setVoucherTitle('');
    setVoucherDescription('');
    setVoucherCallToAction('');
    setVoucherCallToActionUrl('');
    setVoucherDescriptionEditorKey((k) => k + 1);
    setDescriptionAccordionExpanded(true);
    setVoucherName('');
    setVoucherStartDate(dayjs().format('YYYY-MM-DD'));
    setCaseId('');
    setRewardProducts([...DEFAULT_REWARD_PRODUCTS]);
    setMinSpendEnabled(false);
    setMinSpendAmount('100');
    setMinSpendProducts([...DEFAULT_REWARD_PRODUCTS] as Array<
      NonNullable<VoucherReward['min_spend_products']>[number]
    >);
    setValue(DEFAULT_REWARD_VALUE);
    setCurrency('AUD');
    setVoucherUserExpiryMode('date');
    setVoucherUserExpiresAt(getDefaultSimpleVoucherFixedExpiryDate());
    setExpiryDays(defaultExpiryDays.toString());
  };

  const createDisabled = createMutation.isPending;

  const pageShell = (child: React.ReactNode, maxWidth: 'sm' | 'md') =>
    isPopup ? (
      <Box sx={{ px: { xs: 0.5, sm: 1.5 }, py: maxWidth === 'sm' ? 1 : 0 }}>{child}</Box>
    ) : (
      <Container>{child}</Container>
    );

  if (isPromoMetaLoading || !promoMeta) {
    return pageShell(
      <>
        {!isPopup && (
          <Helmet>
            <title>Simple voucher create</title>
          </Helmet>
        )}
        <Stack spacing={2} alignItems="center" justifyContent="center" minHeight={isPopup ? 200 : 320}>
          <Spinner />
          <Typography variant="body2" color="text.secondary">
            Loading voucher configuration…
          </Typography>
        </Stack>
      </>,
      'sm',
    );
  }

  if (createdVoucherId && createdCode) {
    return pageShell(
      <>
        {!isPopup && (
          <Helmet>
            <title>Voucher created</title>
          </Helmet>
        )}
        <Stack
          spacing={4}
          alignItems="center"
          justifyContent="center"
          minHeight={isPopup ? 220 : 400}
          sx={{ py: isPopup ? 1 : 0 }}
        >
          <Stack spacing={2} alignItems="center" width="100%">
            <Alert variant="standard" icon={<CheckCircleOutlineIcon fontSize="inherit" />} sx={{ width: '100%' }}>
              <Typography variant="body2" component="div">
                Voucher <strong>{createdCode}</strong> created.
              </Typography>
            </Alert>
            {successfullyAddedUsers.length > 0 && (
              <Box width="100%">
                <Alert severity="success" icon={<CheckCircleOutlineIcon fontSize="inherit" />} sx={{ mb: 0.5 }}>
                  Successfully added user
                </Alert>
                <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.25}>
                  {successfullyAddedUsers.map((u) => (
                    <Typography key={u.userId} component="li" variant="body2">
                      {u.label}
                      {u.label !== u.userId ? (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          ({u.userId})
                        </Typography>
                      ) : null}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
          <Typography variant="h4" sx={{ wordBreak: 'break-all', textAlign: 'center' }}>
            {createdCode}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Button
              variant="outlined"
              onClick={() => handleCopyVoucherName(createdCode)}
              startIcon={<ContentCopyIcon />}
            >
              Copy to clipboard
            </Button>
            <Button variant="contained" onClick={() => push(`/marketing/vouchers/${createdVoucherId}`)}>
              View voucher
            </Button>
          </Stack>
          <Divider flexItem />
          <Button variant="contained" onClick={resetAll} startIcon={<KeyboardBackspace />} sx={{ alignSelf: 'center' }}>
            Create another voucher
          </Button>
        </Stack>
      </>,
      'md',
    );
  }

  const handleSetVoucherName = (value: string) => {
    setVoucherName(value);
    setVoucherNameErrors(getVoucherNamePatternError(value));
  };

  const voucherText =
    'Must be unique. Letters, numbers, underscores, and dashes only. Client facing if no voucher title set.';
  return pageShell(
    <>
      {!isPopup && (
        <Helmet>
          <title>Simple voucher create</title>
        </Helmet>
      )}

      {!isPopup && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 1, mb: 2 }}>
          <Typography variant="h6">Simple voucher create</Typography>
        </Stack>
      )}

      <Stack spacing={3}>
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Voucher name"
                name="voucher_name"
                required
                fullWidth
                value={voucherName}
                onChange={(e) => handleSetVoucherName(e.target.value?.toUpperCase() ?? '')}
                error={!!voucherNameErrors}
                disabled={createDisabled}
                helperText={voucherNameErrors ? `${voucherNameErrors} ${voucherText}` : voucherText}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Voucher title (client-facing)"
                fullWidth
                value={voucherTitle}
                onChange={(e) => setVoucherTitle(e.target.value)}
                disabled={createDisabled}
                helperText="Client-facing title shown with the voucher. VoucherName is used if no voucher title set."
              />
            </Grid>
            <Grid item xs={12}>
              <Accordion
                expanded={descriptionAccordionExpanded}
                onChange={(_, nextExpanded) => setDescriptionAccordionExpanded(nextExpanded)}
                disableGutters
                variant="outlined"
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ overflow: 'hidden', pr: 1, width: '100%' }}>
                    <Typography component="span" fontWeight={500}>
                      Voucher Description
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {voucherDescriptionSummaryPreview}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <MarkdownEditor
                    key={voucherDescriptionEditorKey}
                    value={voucherDescription}
                    onChange={(next: string) => setVoucherDescription(next)}
                    helperText="Optional description shown to the user to explain voucher details. Supports markdown."
                    disabled={createDisabled}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Voucher call to action"
                fullWidth
                value={voucherCallToAction}
                onChange={(e) => setVoucherCallToAction(e.target.value)}
                disabled={createDisabled}
                helperText="Optional. Overrides the default call-to-action text shown with the voucher before redemption."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Voucher call to action URL"
                fullWidth
                type="url"
                value={voucherCallToActionUrl}
                onChange={(e) => setVoucherCallToActionUrl(e.target.value)}
                disabled={createDisabled}
                helperText="Optional. Destination link for the voucher call-to-action."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Start date"
                type="date"
                fullWidth
                value={voucherStartDate ? dayjs(voucherStartDate).format('YYYY-MM-DD') : ''}
                onChange={(event) => {
                  const localValue = event.target.value;
                  if (!localValue) {
                    setVoucherStartDate('');
                    return;
                  }
                  setVoucherStartDate(dayjs(localValue).format('YYYY-MM-DD'));
                }}
                InputLabelProps={{ shrink: true }}
                disabled={createDisabled}
                helperText="Visible to customers from this date, from midnight Sydney time."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1.5}>
                {voucherUserExpiryMode === 'date' && (
                  <TextField
                    label="Expiry date"
                    type="date"
                    required
                    fullWidth
                    value={voucherUserExpiresAt ? dayjs(voucherUserExpiresAt).format('YYYY-MM-DD') : ''}
                    onChange={(event) => {
                      const localValue = event.target.value;
                      if (!localValue) {
                        setVoucherUserExpiresAt('');
                        return;
                      }
                      setVoucherUserExpiresAt(dayjs(localValue).format('YYYY-MM-DD'));
                    }}
                    InputLabelProps={{ shrink: true }}
                    disabled={createDisabled}
                    helperText="How long until the voucher expires for this user, from midnight Sydney time. Defaults to 365 days."
                  />
                )}
                {voucherUserExpiryMode === 'days' && (
                  <TextField
                    label="Expiry days"
                    type="number"
                    required
                    fullWidth
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(e.target.value)}
                    inputProps={{ min: 0, step: 1 }}
                    disabled={createDisabled}
                    helperText={`Days from today until the voucher expires, at midnight Sydney time. Default ${defaultExpiryDays} days.`}
                  />
                )}
                <RadioGroup
                  row
                  value={voucherUserExpiryMode}
                  onChange={(e) => {
                    const next = e.target.value as VoucherUserExpiryMode;
                    setVoucherUserExpiryMode(next);
                    if (next === 'days') {
                      setVoucherUserExpiresAt('');
                      setExpiryDays(defaultExpiryDays.toString());
                    } else {
                      setExpiryDays('');
                      setVoucherUserExpiresAt((prev) =>
                        prev.trim() ? prev : getDefaultSimpleVoucherFixedExpiryDate(),
                      );
                    }
                  }}
                >
                  <FormControlLabel value="days" control={<Radio />} label="Expiry days" disabled={createDisabled} />
                  <FormControlLabel value="date" control={<Radio />} label="Expiry date" disabled={createDisabled} />
                </RadioGroup>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Reason"
                name="reason"
                fullWidth
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={createDisabled}
                helperText="Reason for creating this voucher (internal only)."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Case ID"
                name="case_id"
                fullWidth
                required={mode === 'popup'}
                value={caseId}
                onChange={(e) => setCaseId(e.target.value)}
                disabled={createDisabled}
                helperText="Not shown to customers. Internal reference only (internal only)."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id="simple-voucher-currency">Currency</InputLabel>
                <Select
                  labelId="simple-voucher-currency"
                  label="Currency"
                  value={currency}
                  onChange={(e) => setCurrency(String(e.target.value))}
                  disabled={createDisabled}
                >
                  {currencyCodes.map((cur) => (
                    <MenuItem key={cur} value={cur}>
                      {cur}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Reward value"
                type="number"
                required
                fullWidth
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={createDisabled}
                inputProps={{ min: 0, step: 'any' }}
                helperText="Discount amount this voucher is worth (only applies to the 'Products' selection)."
              />
            </Grid>
            <Grid item xs={6}>
              <VoucherProductTypeField
                id="simple-voucher-reward-products"
                promoProductTypes={promoMeta.promo_product_types ?? []}
                value={rewardProducts}
                onChange={(next) => setRewardProducts(next as Array<VoucherReward['products'][number]>)}
                disabled={createDisabled}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Same product list as simple promo discount lines. Voucher product type is inferred from your selection
                (one category → that type; several categories → generic).
              </Typography>
              <ProductTypeEligibilityHelper
                promoProductTypes={promoMeta.promo_product_types ?? []}
                context="discount"
                selectedProductValues={rewardProducts}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, pl: 2, borderLeft: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Minimum spend
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Optionally, requires a minimum spend
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={minSpendEnabled}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setMinSpendEnabled(on);
                    if (on && minSpendProducts.length === 0) {
                      setMinSpendProducts([...rewardProducts] as Array<
                        NonNullable<VoucherReward['min_spend_products']>[number]
                      >);
                    }
                  }}
                  disabled={createDisabled}
                />
              }
              label="Set minimum spend"
            />
            {minSpendEnabled && (
              <Stack direction="row" spacing={3} sx={{ mt: 1.5 }}>
                <TextField
                  sx={{ flex: 1 }}
                  label="Minimum spend"
                  type="number"
                  required
                  value={minSpendAmount}
                  onChange={(e) => setMinSpendAmount(e.target.value)}
                  disabled={createDisabled}
                  inputProps={{ min: 0, step: 'any' }}
                  helperText={`In ${currency}. Customer must reach this subtotal on the selected lines before the voucher discount applies.`}
                />
                <FormControl sx={{ flex: 1 }}>
                  <StandaloneMultiProductOptionSelect
                    id="simple-voucher-min-spend-products"
                    promoProductTypes={promoMeta.promo_product_types ?? []}
                    multiSelectType="min_spend_products"
                    value={minSpendProducts}
                    onChange={(next) =>
                      setMinSpendProducts(next as Array<NonNullable<VoucherReward['min_spend_products']>[number]>)
                    }
                    disabled={createDisabled}
                  />
                  <ProductTypeEligibilityHelper
                    promoProductTypes={promoMeta.promo_product_types ?? []}
                    context="minSpend"
                    selectedProductValues={minSpendProducts}
                  />
                </FormControl>
              </Stack>
            )}
          </Box>

          {promoMeta.voucher_toggles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <SummaryToggleAccordion
                title="Voucher Toggles"
                options={promoMeta.voucher_toggles}
                selectedKeys={selectedVoucherToggleKeys}
                onChange={setSelectedVoucherToggleKeys}
                disabled={createDisabled}
              />
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <TextField
              label="Offer ID"
              name="offer_id"
              fullWidth
              value={offerId}
              onChange={(e) => setOfferId(e.target.value)}
              disabled={createDisabled}
              helperText="Optionally limit this voucher to a specific offer."
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {willAutoAssignOnCreate && (
            <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }} icon={false}>
              {assignedUserIds.length === 1 ? (
                <Typography variant="body2" component="span" display="block" gutterBottom>
                  When you create the voucher, this user (<strong>{prefillAssigneeUserLabel}</strong>) will be added to
                  it
                </Typography>
              ) : (
                <Typography variant="body2" component="span" display="block" gutterBottom>
                  When you create the voucher, the {assignedUserIds.length} user(s) listed below will be added to it
                </Typography>
              )}
            </Alert>
          )}

          <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
              Assign to users (optional)
            </Typography>
            {assignRows.map((row) => (
              <Stack key={row.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <UserSearchWidget
                    id={`simple-voucher-user-${row.id}`}
                    label="Name / Email / Customer Support Code / User ID"
                    formErrors={[]}
                    disabled={createDisabled}
                    onChange={(_, value) =>
                      updateAssignRow(row.id, {
                        userId: value,
                        ...(value === '' ? { userSearchLabel: '' } : {}),
                      })
                    }
                    passedAutocompleteValue={row.userSearchLabel}
                    setPassedAutocompleteValue={(next) => {
                      setAssignRows((rows) =>
                        rows.map((r) => {
                          if (r.id !== row.id) {
                            return r;
                          }
                          const label = typeof next === 'function' ? next(r.userSearchLabel) : next;
                          return { ...r, userSearchLabel: label };
                        }),
                      );
                    }}
                  />
                </Box>
                <IconButton aria-label="Remove user row" onClick={() => removeAssignRow(row.id)} size="small">
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" variant="text" onClick={addAssignRow} disabled={Boolean(createdVoucherId)}>
              {assignRows.length > 0 ? 'Add another user' : 'Add user'}
            </Button>
          </Stack>

          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <LoadingButton variant="contained" loading={createMutation.isPending} onClick={() => void handleCreate()}>
              {willAutoAssignOnCreate
                ? `Create voucher and add user${assignedUserIds.length > 1 ? 's' : ''}`
                : 'Create voucher'}
            </LoadingButton>
          </Box>
        </Box>
      </Stack>
      <Box height={100} />
    </>,
    'md',
  );
}

export default withTenant(SimpleVoucherForm);
